import React, { useState } from 'react';
import axios from 'axios';
import { 
  Pill, 
  FileText, 
  Activity, 
  HelpCircle, 
  Clock, 
  AlertTriangle,
  Check,
  ChevronRight,
  Upload
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Category-specific confirmation messages
const CONFIRMATION_MESSAGES = {
  prescription_refill: {
    title: 'Refill Request Received',
    paragraphs: [
      'Your prescription refill request has been received and will be reviewed by your care team within 1,2 business days.',
      'Refills are processed as long as treatment remains clinically appropriate and your membership is active. In some cases, updated labs or additional information may be required before approval.',
      'We\'ll follow up with next steps or confirmation once your request has been reviewed.'
    ]
  },
  lab_upload: {
    title: 'Lab Results Received',
    paragraphs: [
      'Thank you for submitting your lab results. Your provider will review them asynchronously within 1,2 business days.',
      'Most lab reviews do not require a visit,your provider will reach out only if adjustments or follow-up are needed.',
      'If you have specific questions about your results, feel free to submit a separate message.'
    ]
  },
  lab_question: {
    title: 'Lab Question Received',
    paragraphs: [
      'Your question about labs has been received and will be reviewed within 1,2 business days.',
      'Your provider will guide you on next steps, including whether lab work is recommended based on your current care plan.',
      'If labs are needed, we\'ll provide instructions on how to complete them.'
    ]
  },
  symptom_update: {
    title: 'Symptom Update Received',
    paragraphs: [
      'Thank you for sharing this update with your care team. Tracking your symptoms helps us understand your progress and guide any adjustments to your care.',
      'Your provider will review this information within 1,2 business days and follow up if any changes are recommended.',
      'Continue to note any patterns or changes,this information is valuable for optimizing your treatment.'
    ]
  },
  medication_question: {
    title: 'Medication Question Received',
    paragraphs: [
      'Your question has been received and will be reviewed by your care team within 1,2 business days.',
      'If your question involves potential safety concerns, we\'ll prioritize a prompt response.',
      'For immediate concerns about a reaction or side effect, please contact your local pharmacy or seek medical attention.'
    ]
  }
};

const MESSAGE_CATEGORIES = [
  {
    id: 'prescription_refill',
    title: 'Prescription Refill Request',
    icon: Pill,
    description: 'Request a refill for your current prescription.',
    guidance: 'Please include the medication name, dosage, and pharmacy preference. Note any changes in your condition since your last visit.',
    hasUpload: false
  },
  {
    id: 'lab_question',
    title: 'Lab Upload or Lab Question',
    icon: FileText,
    description: 'Submit recent lab results or ask questions about your labs.',
    guidance: 'Upload your lab results or describe your question. Include the date of the labs and any specific values you have questions about.',
    hasUpload: true
  },
  {
    id: 'symptom_update',
    title: 'Symptom Update',
    icon: Activity,
    description: 'Report new or changing symptoms to your provider.',
    guidance: 'Describe your symptoms, when they started, their severity (1-10), and any patterns you\'ve noticed. Include what makes them better or worse.',
    hasUpload: false
  },
  {
    id: 'medication_question',
    title: 'Medication Question',
    icon: HelpCircle,
    description: 'Ask questions about your current medications or treatments.',
    guidance: 'Include the medication name and your specific question. Common topics include side effects, interactions, timing, or dosage concerns.',
    hasUpload: false
  }
];

const PatientMessages = () => {
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    subject: '',
    message: '',
    hasLabUpload: false
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [confirmationType, setConfirmationType] = useState(null);
  const [error, setError] = useState(null);

  const handleCategorySelect = (category) => {
    setSelectedCategory(category);
    setFormData(prev => ({
      ...prev,
      subject: category.title,
      hasLabUpload: false
    }));
    setSubmitSuccess(false);
    setConfirmationType(null);
    setError(null);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (type === 'checkbox') {
      setFormData(prev => ({
        ...prev,
        [name]: checked
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;

    setIsSubmitting(true);
    setError(null);

    try {
      // Determine confirmation type based on category and whether labs were uploaded
      let confType = selectedCategory.id;
      if (selectedCategory.id === 'lab_question') {
        confType = formData.hasLabUpload ? 'lab_upload' : 'lab_question';
      }

      await axios.post(`${API}/messages`, {
        category: selectedCategory.id,
        email: formData.email,
        firstName: formData.firstName,
        lastName: formData.lastName,
        subject: formData.subject,
        message: formData.message,
        hasLabUpload: formData.hasLabUpload
      });

      setConfirmationType(confType);
      setSubmitSuccess(true);
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        subject: '',
        message: '',
        hasLabUpload: false
      });
    } catch (err) {
      console.error('Error submitting message:', err);
      setError('Unable to submit your message. Please try again or contact support.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBack = () => {
    setSelectedCategory(null);
    setSubmitSuccess(false);
    setConfirmationType(null);
    setError(null);
  };

  const confirmation = confirmationType ? CONFIRMATION_MESSAGES[confirmationType] : null;

  return (
    <div className="page-messages">
      <div className="messages-container">
        {/* Header */}
        <div className="messages-header">
          <h1 className="heading-2">Patient Portal</h1>
          <p className="body-regular">
            Submit a request to your care team. Select a category below to get started.
          </p>
        </div>

        {/* Important Notice */}
        <div className="messages-notice">
          <div className="notice-content">
            <Clock className="notice-icon" />
            <div>
              <p className="notice-text">
                <strong>Messages are reviewed within 1,2 business days.</strong>
              </p>
              <p className="notice-subtext">
                This portal is for non-urgent requests only.
              </p>
            </div>
          </div>
          <div className="notice-urgent">
            <AlertTriangle className="urgent-icon" />
            <p>
              <strong>For urgent or emergency issues,</strong> please call 911 or visit your nearest emergency room.
            </p>
          </div>
        </div>

        {/* Category-Specific Success Message */}
        {submitSuccess && confirmation && (
          <div className="messages-success">
            <div className="success-content">
              <div className="success-icon-circle">
                <Check className="success-check" />
              </div>
              <h2 className="heading-3">{confirmation.title}</h2>
              <div className="confirmation-message">
                {confirmation.paragraphs.map((paragraph, index) => (
                  <p key={index} className="body-regular">{paragraph}</p>
                ))}
              </div>
              <div className="confirmation-reminder">
                <AlertTriangle className="reminder-icon" />
                <p>
                  If you experience any urgent symptoms or concerns, please seek immediate medical care.
                </p>
              </div>
              <button onClick={handleBack} className="btn-primary">
                Submit Another Request
              </button>
            </div>
          </div>
        )}

        {/* Category Selection */}
        {!selectedCategory && !submitSuccess && (
          <div className="category-grid">
            {MESSAGE_CATEGORIES.map((category) => {
              const IconComponent = category.icon;
              return (
                <button
                  key={category.id}
                  className="category-card"
                  onClick={() => handleCategorySelect(category)}
                >
                  <div className="category-icon-wrapper">
                    <IconComponent className="category-icon" />
                  </div>
                  <div className="category-content">
                    <h3 className="category-title">{category.title}</h3>
                    <p className="category-description">{category.description}</p>
                  </div>
                  <ChevronRight className="category-arrow" />
                </button>
              );
            })}
          </div>
        )}

        {/* Message Form */}
        {selectedCategory && !submitSuccess && (
          <div className="message-form-container">
            <button onClick={handleBack} className="back-link">
              ← Back to categories
            </button>

            <div className="message-form-card">
              <div className="form-category-header">
                <div className="category-icon-wrapper-small">
                  {React.createElement(selectedCategory.icon, { className: 'category-icon-small' })}
                </div>
                <h2 className="heading-3">{selectedCategory.title}</h2>
              </div>

              <div className="form-guidance">
                <p>{selectedCategory.guidance}</p>
              </div>

              <form onSubmit={handleSubmit} className="message-form">
                <div className="form-row">
                  <div className="form-field">
                    <label htmlFor="firstName" className="form-label">First Name *</label>
                    <input
                      type="text"
                      id="firstName"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleChange}
                      required
                      className="form-input"
                    />
                  </div>
                  <div className="form-field">
                    <label htmlFor="lastName" className="form-label">Last Name *</label>
                    <input
                      type="text"
                      id="lastName"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleChange}
                      required
                      className="form-input"
                    />
                  </div>
                </div>

                <div className="form-field">
                  <label htmlFor="email" className="form-label">Email Address *</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="form-input"
                    placeholder="Use the email associated with your account"
                  />
                </div>

                <div className="form-field">
                  <label htmlFor="subject" className="form-label">Subject *</label>
                  <input
                    type="text"
                    id="subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    required
                    className="form-input"
                  />
                </div>

                {/* Lab Upload Checkbox for Lab Question category */}
                {selectedCategory.hasUpload && (
                  <div className="form-field">
                    <label className="upload-checkbox-label">
                      <input
                        type="checkbox"
                        name="hasLabUpload"
                        checked={formData.hasLabUpload}
                        onChange={handleChange}
                        className="upload-checkbox"
                      />
                      <Upload className="upload-icon" />
                      <span>I am uploading or attaching lab results with this message</span>
                    </label>
                    <p className="upload-hint">
                      If uploading labs, please include them as an attachment or paste the values in your message below.
                    </p>
                  </div>
                )}

                <div className="form-field">
                  <label htmlFor="message" className="form-label">Your Message *</label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows="6"
                    className="form-input"
                    placeholder={selectedCategory.guidance}
                  />
                </div>

                {error && (
                  <div className="form-error-submit">
                    <p>{error}</p>
                  </div>
                )}

                <div className="form-submit">
                  <button 
                    type="submit" 
                    className="btn-primary btn-submit"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? 'Submitting...' : 'Submit Request'}
                  </button>
                </div>

                <p className="form-disclaimer">
                  By submitting this form, you confirm that you are an active Glow Health member. 
                  Your message will be reviewed by our care team and routed to the appropriate provider.
                </p>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PatientMessages;
